import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
//local
import {CustomTooltip} from "components/CustomTooltip";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {InitialsBadge} from "components/InitialsBadge";
import {CustomBadge} from "components/CustomBadge";
import {RftProposalInterestedStatusModal} from "components/modals/RftProposalInterestedStatusModal";
import {RftProposalCreateOfferModal} from "components/modals/RftProposalCreateOfferModal";
import {LockValue} from "components/LockValue";
import {hasPermission, getFullName} from "helpers/helper";
import {useTranslation} from "react-i18next";
import {
  IconTrumbsUp,
  IconTrumbsDown,
  IconContract,
  IconCheckMark,
  IconSkullEmpty,
  IconUE2021,
  IconRamavtal,
  IconUreadStatus,
  IconHourglass,
  IconBullHorn,
  IconHandOnRight,
  IconTabletPen,
  IconEdit,
} from "components/icons";
import {toRequestId, toOrganizationId} from "constants/routes.const";
import {ourAgreementsTypes} from "constants/user.consts";
import {color} from "constants/color.consts";
import cn from "classnames";

const OK_ICON = 1;
const NO_ICON = 4;

const ProcurementDetails = ({
  recipient,
  rfpId,
  isFavorite,
  isBlacklisted,
  isContract,
  isPublic,
  contractSellerOrganizationId,
  isSignedContract,
  onClickContract,
  onClickProposal,
  onClickCreateOffer,
  onUpdateProposal,
}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [isRftProposalInterestedStatusModal, setRftProposalInterestedStatusModal] =
    useState(false);
  const [isRftProposalCreateOfferModal, setRftProposalCreateOfferModal] = useState(false);

  const organizationId = recipient.Organization?.id;
  const isReaded = recipient?.readed ? OK_ICON : NO_ICON;
  const hasOffer = !!recipient?.Offer;
  const isUnreadOffer = hasOffer && !recipient.Offer.readed && !isContract;
  const UserAssignee = recipient?.UserAssignee;
  const unreadMessageCount = recipient?.MessagesStat?.unreadMessageCount ?? 0;
  const totalMessageCount = recipient?.MessagesStat?.totalMessageCount ?? 0;

  const ourAgreements = recipient?.Organization?.OurAgreements;
  const isUE2021 = !!ourAgreements?.find(
    (item) => item?.type === ourAgreementsTypes.UE2021
  );
  const isRamavtal = !!ourAgreements?.find(
    (item) => item?.type === ourAgreementsTypes.RAMAVTAL
  );

  const rowColor = isPublic ? color.green : color.blue;
  const statusIcon = {
    [null]: <IconEdit color={color.blue} />,
    [false]: <IconTrumbsDown />,
    [true]: <IconTrumbsUp />,
  };

  const handleMessageBadgeClick = () => {
    if (!rfpId) return;
    const state = {messageProposalId: recipient.Organization.id};
    navigate(toRequestId(rfpId), {state});
  };

  return (
    <div
      className={cn("project-view-procurements-table_item__recipient", {
        "disable-response": !hasPermission(["EDIT_PROCUREMENT_SUPPLIER_RESPONSE"]),
      })}
    >
      <div className="project-view-procurements-table_item__details__title mr-4">
        {isPublic ? (
          <IconBullHorn size="16" addClass="mr-2" />
        ) : (
          <IconHandOnRight height={16} width={15} addClass="mr-2" />
        )}
        <span
          className={`${isUnreadOffer ? "__bold " : ""}${
            isPublic ? "green" : hasOffer ? "blue" : "black"
          }`}
          onClick={() => (hasOffer ? onClickProposal() : undefined)}
        >
          {recipient?.Organization?.name}
        </span>
        {isFavorite && <IconCheckMark color={rowColor} addClass="ml-2" />}
        {isBlacklisted && (
          <IconSkullEmpty color={rowColor} height={20} width={20} addClass="ml-2" />
        )}
        {isUE2021 && <IconUE2021 color={rowColor} addClass="ml-2" />}
        {isRamavtal && <IconRamavtal color={rowColor} addClass="ml-2" />}
        <a
          href={toOrganizationId(recipient?.Organization?.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="fg2 pop-company-profile ml-4 cpointer black __text-right"
        >
          ⧉ {t("POP_COMPANY_PROFILE_IN_PROJECT_VIEW_LABEL")}
        </a>
      </div>
      <div className="project-view-procurements-table_item__details__icon">
        <div className={`indicator_sub-status __${isReaded}`} />
      </div>
      <div className="project-view-procurements-table_item__details__icon">
        <LockValue
          canPermissions={["EDIT_PROCUREMENT_SUPPLIER_RESPONSE"]}
          needLabel
          noLockIcon
          needShowModalToSubscribe
        >
          <ButtonTransparent
            iconButton
            icon={statusIcon[recipient?.interested]}
            noBorder
            showOnHover={recipient?.interested === null}
            tooltip={
              recipient?.interested === null
                ? "RFT_PROPOSAL_INTEREST_STATUS_TOOLTIP"
                : recipient?.interested === false
                ? recipient?.rejectMessage
                : ""
            }
            onClick={() =>
              hasPermission(["EDIT_PROCUREMENT_SUPPLIER_RESPONSE"]) &&
              setRftProposalInterestedStatusModal(true)
            }
          />
        </LockValue>
      </div>
      <div className="project-view-procurements-table_item__details__icon">
        {UserAssignee && (
          <CustomTooltip
            text={`${getFullName(UserAssignee.firstName, UserAssignee.lastName)}\n${
              UserAssignee.mobilePhone
            }\n${UserAssignee.email}`}
            addClass="organization-card_body__indicator__user-tooltip"
          >
            <span>
              <InitialsBadge
                firstName={UserAssignee.firstName}
                lastName={UserAssignee.lastName}
              />
            </span>
          </CustomTooltip>
        )}
      </div>
      <div className="project-view-procurements-table_item__details__icon">
        {hasOffer ? (
          <ButtonTransparent
            iconButton
            icon={<IconTabletPen color={rowColor} />}
            noBorder
            onClick={onClickProposal}
          />
        ) : (
          <ButtonTransparent
            iconButton
            icon={<IconEdit color={color.blue} />}
            noBorder
            showOnHover
            tooltip="RFT_PROPOSAL_NOOFFER_TOOLTIP"
            onClick={() => setRftProposalCreateOfferModal(true)}
          />
        )}
        {isUnreadOffer && (
          <IconUreadStatus addClass="project-view-procurements-table_item__details__icon__unread-icon" />
        )}
      </div>
      <div className="project-view-procurements-table_item__details__icon">
        {isContract && contractSellerOrganizationId === organizationId && (
          <ButtonTransparent
            icon={
              isSignedContract ? (
                <IconContract color={rowColor} />
              ) : (
                <IconHourglass color={rowColor} />
              )
            }
            noBorder
            onClick={onClickContract}
          />
        )}
      </div>
      <div className="project-view-procurements-table_item__details__messages">
        <CustomBadge
          count={totalMessageCount}
          color="midGray"
          addClass="mr-1"
          onClick={handleMessageBadgeClick}
        />
        <CustomBadge
          count={unreadMessageCount}
          color="red"
          onClick={handleMessageBadgeClick}
        />
      </div>
      {isRftProposalInterestedStatusModal && (
        <RftProposalInterestedStatusModal
          interested={recipient?.interested}
          onClose={() => setRftProposalInterestedStatusModal(false)}
          onButtonClick={(values) => onUpdateProposal(recipient.id, values)}
        />
      )}
      {isRftProposalCreateOfferModal && (
        <RftProposalCreateOfferModal
          onClose={() => setRftProposalCreateOfferModal(false)}
          onSubmit={onClickCreateOffer}
        />
      )}
    </div>
  );
};

export default ProcurementDetails;
